import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronUp, Monitor, Trash2 } from 'react-feather';
import { useConfirm } from 'material-ui-confirm';

import { Wrapper } from '../styles';
import { notifyMsg } from '../../../configs';
import { useSnack } from '../../../util/useSnack';
import { displayDistance } from '../../../util/dateTime';
import { useDeleteDevice } from '../../../api-http/devices';

interface DeviceInfoProps {
  data: any;
}

const DeviceInfo: FC<DeviceInfoProps> = ({ data }) => {
  const { name, deviceId, status, deviceInfo, lastOnline } = data || {};
  const {
    brand,
    resolution,
    touchEnable,
    manufacturer,
    ipAddress,
    userAgent,
    appVersion,
    totalSpace,
    freeSpace,
  } = deviceInfo || {};
  const [technicalDetailsOpen, setTechnicalDetailsOpen] = useState(false);

  const confirm = useConfirm();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const navigate = useNavigate();

  const { error: deleteError, mutate: removeDevice } = useDeleteDevice({
    onSuccess: () => {
      navigate('/devices');
      showSuccessSnack(notifyMsg.DEVICE_DELETED_SUCCESSFULLY);
    },
    onError: () =>
      deleteError
        ? showErrSnack(deleteError.msg)
        : showErrSnack(notifyMsg.FAILED_TO_REMOVE_DEVICE),
  });

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: 'Are you sure you want to remove this device?',
        confirmationText: 'Yes',
      });
      removeDevice({ id });
    } catch (err) {}
  };

  const bytesToGb = (value?: number) =>
    typeof value === 'number' ? `${(value / 1000000000).toFixed(2)} GB` : '—';
  const freeSpacePercent =
    typeof freeSpace === 'number' &&
    typeof totalSpace === 'number' &&
    totalSpace
      ? Math.min(100, Math.max(0, (freeSpace / totalSpace) * 100))
      : 0;

  const Detail = ({
    label,
    value,
  }: {
    label: string;
    value?: React.ReactNode;
  }) => (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{ color: '#0f172a', fontSize: 14, overflowWrap: 'anywhere' }}
      >
        {value || '—'}
      </Typography>
    </Stack>
  );

  return (
    <Wrapper>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={2}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Monitor size={21} color="#2563eb" />
          <Typography
            component="h2"
            sx={{ color: '#0f172a', fontSize: 20, fontWeight: 700 }}
          >
            {name}
          </Typography>
          <Chip
            label={status ? 'Online' : 'Offline'}
            size="small"
            sx={{
              bgcolor: status ? '#dcfce7' : '#fee2e2',
              color: status ? '#15803d' : '#b91c1c',
              fontWeight: 700,
            }}
          />
        </Stack>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Trash2 size={16} />}
          onClick={() => handleDelete(deviceId)}
        >
          Remove device
        </Button>
      </Stack>
      <Divider sx={{ my: 2.5 }} />
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 2.5,
        }}
      >
        <Detail label="Device ID" value={deviceId} />
        <Detail label="Brand" value={brand} />
        <Detail label="Resolution" value={resolution} />
        <Detail label="Last online" value={displayDistance(lastOnline)} />
        <Detail label="Touch enabled" value={touchEnable ? 'Yes' : 'No'} />
      </Stack>

      <Divider sx={{ mt: 2.5 }} />
      <Button
        variant="text"
        size="small"
        endIcon={
          technicalDetailsOpen ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )
        }
        aria-expanded={technicalDetailsOpen}
        aria-controls="device-technical-details"
        onClick={() => setTechnicalDetailsOpen((open) => !open)}
        sx={{ mt: 1, px: 0.5, color: '#475569' }}
      >
        Technical details
      </Button>
      <Collapse in={technicalDetailsOpen}>
        <Box
          id="device-technical-details"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2.5,
            pt: 1.5,
          }}
        >
          <Detail label="Manufacturer" value={manufacturer} />
          <Detail label="IP address" value={ipAddress} />
          <Detail label="App version" value={appVersion} />
          <Box>
            <Detail
              label="Storage"
              value={`${bytesToGb(freeSpace)} free of ${bytesToGb(totalSpace)}`}
            />
            {freeSpacePercent > 0 && (
              <LinearProgress
                variant="determinate"
                value={freeSpacePercent}
                aria-label={`${freeSpacePercent.toFixed(0)}% storage free`}
                sx={{ mt: 1, maxWidth: 220, height: 5, borderRadius: 999 }}
              />
            )}
          </Box>
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Detail label="User agent" value={userAgent} />
          </Box>
        </Box>
      </Collapse>
    </Wrapper>
  );
};

export default DeviceInfo;
